import { Formik } from 'formik';
import { useState } from 'react';

import { Messages } from 'config';
import { formatPrice } from 'utils/helpers';
import { useNavigate, handleMutation } from 'hooks';
import { useChangeStatusMutation } from 'store/api/payout';
import { PayoutType, PayoutStatus, ButtonColors } from 'types';
import {
  Icon,
  Menu,
  Dialog,
  Button,
  InputField,
  DialogType,
  SelectField,
  ButtonTypes,
  CommonStyles,
  ButtonWithRef,
} from 'components';

import { EditDecisionDialogProps } from './types';
import {
  decisionOptions,
  getInitialValues,
  EditDecisionValues,
  getValidationSchema,
} from './config';

// Owner 19 Oct 2026: an approved, partially approved or rejected item can be
// corrected until it is paid. One edit icon opens this dialog; the decision
// is changed here (approve, partial approve or reject). Where the item itself
// can also be edited (onEditItem), the same single pencil opens a small menu,
// "Edit item" / "Edit decision": never two pencils (owner 19 Oct).
export const EditDecisionDialog = ({
  data,
  onEditItem,
  withNavigate,
  type = PayoutType.Reimbursements,
}: EditDecisionDialogProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const requestedAmount = Number(data.requestAmount ?? data.amount);
  const eventName = data.event?.name?.trim();

  const [changeStatus, mutation] = useChangeStatusMutation();

  handleMutation({
    ...mutation,
    successMessage: `${type} status ${Messages.UPDATED_SUCCESSFULLY}`,
  });

  // the same 40x40 icon button as Approve / Partial / Reject, on every width
  const trigger = onEditItem ? (
    // No tooltip on this trigger: the tooltip's own popup takes the button's
    // ref, and the menu then opens at the corner of the screen.
    <Menu
      buttonAlignStart
      keepTooltipInside
      position={['bottom right', 'bottom left', 'top right', 'top left']}
      buttonProps={{
        icon: <Icon.Edit />,
        testId: 'payout-edit-menu',
        colorType: ButtonColors.LightBlue,
      }}
      data={[
        {
          text: 'Edit item',
          icon: <Icon.Edit />,
          onClick: onEditItem,
          testId: 'payout-edit-item',
        },
        {
          icon: <Icon.Edit />,
          text: 'Edit decision',
          onClick: () => setOpen(true),
          testId: 'payout-edit-decision',
        },
      ]}
    />
  ) : (
    <ButtonWithRef
      icon={<Icon.Edit />}
      tooltip="Edit decision"
      onClick={() => setOpen(true)}
      testId="payout-edit-decision"
      colorType={ButtonColors.LightBlue}
    />
  );

  return (
    <>
      {trigger}
      <Dialog
        open={open}
        title="Edit decision"
        closeOnDocumentClick={false}
        onClose={() => setOpen(false)}
      >
        {(close, _, ButtonWrap) => (
          <Formik<EditDecisionValues>
            validationSchema={getValidationSchema(requestedAmount)}
            initialValues={getInitialValues({
              amount: data.amount,
              status: data.status,
              reasonReject: data.reasonReject,
            })}
            onSubmit={async ({ amount, status, reasonReject }) => {
              await changeStatus({
                status,
                id: data.id,
                ...(status === PayoutStatus.PartialApproved && {
                  amount: +amount,
                }),
                ...(status === PayoutStatus.NotApproved && {
                  reasonReject: reasonReject.trim(),
                }),
              }).unwrap();
              close();
              if (withNavigate) {
                navigate(-1);
              }
            }}
          >
            {({ dirty, values, isSubmitting }) => (
              <CommonStyles.Form gap={24}>
                <p>
                  Requested amount <b>{formatPrice(requestedAmount)}</b>
                  {eventName && (
                    <>
                      {' at '}
                      <b>{eventName}</b>
                    </>
                  )}
                  {'. The new decision replaces the current one.'}
                </p>

                <SelectField
                  onlyValue
                  name="status"
                  label="Decision"
                  options={decisionOptions}
                />

                {values.status === PayoutStatus.PartialApproved && (
                  <InputField
                    number
                    decimal
                    name="amount"
                    label="Approved Amount"
                    controlErrorLabel={false}
                    placeholder="Add Approved Amount"
                  />
                )}

                {values.status === PayoutStatus.NotApproved && (
                  <InputField
                    type="textarea"
                    controlErrorLabel
                    name="reasonReject"
                    label="Rejection reason"
                    placeholder="Add a rejection reason"
                  />
                )}

                <ButtonWrap type={DialogType.Plain}>
                  <Button
                    text="Cancel"
                    onClick={close}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    text="Save"
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                  />
                </ButtonWrap>
              </CommonStyles.Form>
            )}
          </Formik>
        )}
      </Dialog>
    </>
  );
};
